<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);

// GK-HUB: OGG/OPUS branch — opus-recorder captura direto em .ogg p/ WhatsApp voice messages
const isOggMode = props.audioRecordFormat === 'audio/ogg';
let opusRecorder = null;
let opusProgressInterval = null;
let opusStartedAt = 0;

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const extensionFor = mime => {
  if (mime === 'audio/ogg') return 'ogg';
  if (mime === 'audio/wav') return 'wav';
  return 'mp3';
};

const emitFinish = blob => {
  const audioUrl = URL.createObjectURL(blob);
  const fileName = `${getUuid()}.${extensionFor(props.audioRecordFormat)}`;
  const file = new File([blob], fileName, { type: props.audioRecordFormat });
  if (wavesurfer.value) wavesurfer.value.load(audioUrl);
  emit('finishRecord', {
    name: file.name,
    type: file.type,
    size: file.size,
    file,
  });
  hasRecording.value = true;
  isRecording.value = false;
};

const initWaveSurfer = () => {
  // GK-HUB: em modo OGG, criamos wavesurfer SEM RecordPlugin (captura é via opus-recorder)
  const plugins = isOggMode
    ? []
    : [
        RecordPlugin.create({
          scrollingWaveform: true,
          renderRecordedAudio: false,
        }),
      ];

  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins,
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));
  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  if (isOggMode) return;

  record.value = wavesurfer.value.plugins[0];

  record.value.on('record-end', async blob => {
    const audioBlob = await convertAudio(blob, props.audioRecordFormat);
    emitFinish(audioBlob);
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

// GK-HUB: OGG/OPUS recording via opus-recorder (output direto sem re-encode)
const startOpusRecording = async () => {
  const { default: Recorder } = await import('opus-recorder');
  const encoderUrl = (
    await import('opus-recorder/dist/encoderWorker.min.js?url')
  ).default;

  opusRecorder = new Recorder({
    encoderPath: encoderUrl,
    encoderSampleRate: 48000,
    numberOfChannels: 1,
    encoderApplication: 2049, // VoIP — otimizado para fala
    streamPages: false,
  });

  opusRecorder.ondataavailable = typedArray => {
    const blob = new Blob([typedArray], { type: 'audio/ogg' });
    emitFinish(blob);
  };

  await opusRecorder.start();
  isRecording.value = true;
  opusStartedAt = Date.now();
  opusProgressInterval = setInterval(() => {
    emit(
      'recorderProgressChanged',
      formatTimeProgress(Date.now() - opusStartedAt)
    );
  }, 250);
};

const stopOpusRecording = () => {
  if (opusProgressInterval) {
    clearInterval(opusProgressInterval);
    opusProgressInterval = null;
  }
  if (opusRecorder) opusRecorder.stop();
};

const stopRecording = () => {
  if (!isRecording.value) return;
  if (isOggMode) {
    stopOpusRecording();
    isRecording.value = false;
  } else {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  if (isOggMode) {
    startOpusRecording().catch(err => {
      // eslint-disable-next-line no-console
      console.error('GK-HUB: opus-recorder failed to start', err);
      isRecording.value = false;
    });
  } else {
    record.value.startRecording();
    isRecording.value = true;
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (opusProgressInterval) clearInterval(opusProgressInterval);
  if (opusRecorder) {
    try {
      opusRecorder.close();
    } catch (_) {
      /* noop */
    }
    opusRecorder = null;
  }
  if (wavesurfer.value) wavesurfer.value.destroy();
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
